import type { PasswordPolicy } from '@/hooks/usePasswordPolicy';

/**
 * Client-side mirrors of backend internal/passwordpolicy (QA #63) and
 * internal/users/username.go (QA #71). They exist so the form can say what is
 * wrong before the round-trip; the backend remains the rule that counts and
 * answers with the same wording.
 */

const CLASS_TESTS: { name: string; test: (ch: string) => boolean }[] = [
  { name: '大寫字母', test: (ch) => /\p{Lu}/u.test(ch) },
  { name: '小寫字母', test: (ch) => /\p{Ll}/u.test(ch) },
  { name: '數字', test: (ch) => /\p{Nd}/u.test(ch) },
  { name: '符號', test: (ch) => !/\p{Lu}|\p{Ll}|\p{Nd}/u.test(ch) },
];

export const PASSWORD_MIN_CLASSES = 3;

/** Human description of the password rule for helper texts. */
export function passwordRuleText(policy: PasswordPolicy): string {
  const base = `至少 ${policy.minLength} 個字元`;
  return policy.requireComplexity
    ? `${base}，且大寫字母、小寫字母、數字、符號四類中至少 ${PASSWORD_MIN_CLASSES} 類，不可包含帳號名稱`
    : base;
}

/** Returns the problem with a candidate password, or '' when it passes the client-side rules. */
export function passwordProblem(password: string, policy: PasswordPolicy, username?: string): string {
  const chars = [...password];
  if (chars.length < policy.minLength) {
    return `密碼至少需要 ${policy.minLength} 個字元（目前 ${chars.length} 個）`;
  }
  if (policy.requireComplexity) {
    const have = CLASS_TESTS.filter((c) => chars.some(c.test)).map((c) => c.name);
    if (have.length < PASSWORD_MIN_CLASSES) {
      return `密碼需包含大寫字母、小寫字母、數字、符號四類中至少 ${PASSWORD_MIN_CLASSES} 類（目前只有：${have.length ? have.join('、') : '無'}）`;
    }
    const u = (username ?? '').trim().toLowerCase();
    if ([...u].length >= 3 && password.toLowerCase().includes(u)) {
      return '密碼不可包含帳號名稱';
    }
  }
  return '';
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Returns the problem with an e-mail address, or '' when it passes (QA #37 /
 * #68). With `required` false an empty value passes, for live checks while
 * the person is still typing. The backend's `email` rule answers with the
 * same shape of wording.
 */
export function emailProblem(email: string, required = true): string {
  const v = email.trim();
  if (!v) return required ? '請輸入電子信箱（例如 name@example.com）' : '';
  return EMAIL_RE.test(v) ? '' : '電子信箱格式不正確，請輸入完整的電子信箱（例如 name@example.com）';
}

/** "@mail.mil.tw" / "@a、@b" — the same rendering as the backend's reason. */
export function emailDomainsText(domains: string[]): string {
  return domains.map((d) => `@${d}`).join('、');
}

/**
 * QA #91 / #94: the problem with an address outside the deployment's allowed
 * domains, or '' (also when no domain is configured). Same wording as the
 * backend (pkg/emaildomain).
 */
export function emailDomainProblem(email: string, domains: string[]): string {
  const v = email.trim();
  if (!v || domains.length === 0) return '';
  const domain = v.slice(v.lastIndexOf('@') + 1).toLowerCase();
  return domains.includes(domain) ? '' : `僅允許使用 ${emailDomainsText(domains)} 的信箱`;
}

export const USERNAME_MIN_LENGTH = 4;
export const USERNAME_MAX_LENGTH = 32;
export const USERNAME_RULE_TEXT = `${USERNAME_MIN_LENGTH}–${USERNAME_MAX_LENGTH} 個字元，只能用小寫英文字母、數字與 . _ - @，須以英文字母開頭（大寫會自動轉成小寫）`;

/** Lower-cases and trims an account name the way the backend stores it. */
export function normalizeUsername(s: string): string {
  return s.trim().toLowerCase();
}

/** Returns the problem with an (already normalised) account name, or ''. */
export function usernameProblem(username: string): string {
  const n = [...username].length;
  if (n < USERNAME_MIN_LENGTH) return `帳號至少需要 ${USERNAME_MIN_LENGTH} 個字元（目前 ${n} 個）`;
  if (n > USERNAME_MAX_LENGTH) return `帳號最多 ${USERNAME_MAX_LENGTH} 個字元（目前 ${n} 個）`;
  if (!/^[a-z]/.test(username)) return '帳號必須以英文字母開頭';
  if (!/^[a-z][a-z0-9._@-]*$/.test(username)) return '帳號只能包含小寫英文字母、數字與 . _ - @';
  return '';
}
